import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle, MoreHorizontal, Sparkles } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@v2/components/ui/dropdown-menu";
import { supabaseV2 } from "../../lib/supabase";
import { usePermissions } from "../../lib/permissions";
import { eaPlatformLabel } from "../../lib/labels";
import { CURRENT_EA_GAME, eaGameLabel, eaGameOf, isLegacyEaLink } from "../../lib/ea-game";
import { buildRosterLookup, fixEaText, type EaLinkRosterRow } from "../../lib/ea-name";
import { track } from "../../lib/analytics";
import { currentIntlLocale } from "../../lib/i18n";
import { EmptyState, SectionLoading } from "../../components/state";
import { TopMembersCard } from "./TopMembersCard";
import { EaClubSection } from "./EaClubSection";
import { SeasonPlayoffTab } from "./SeasonPlayoffTab";
import { FriendlyTab } from "./FriendlyTab";

export function EaWorldView({
  teamId,
  eaClubId,
  eaPlatform,
  eaGame,
  onOpenSettings,
}: {
  teamId: string;
  eaClubId: string | null;
  eaPlatform: string;
  eaGame: string | null;
  onOpenSettings?: () => void;
}) {
  const { t } = useTranslation("mondofc");
  const queryClient = useQueryClient();
  const { canManage } = usePermissions();
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [tab, setTab] = useState<"overview" | "season" | "friendly">("overview");
  // Titolo EA mostrato nelle schede partite; null = quello di default.
  const [gameFilter, setGameFilter] = useState<string | null>(null);

  const rosterQuery = useQuery({
    queryKey: ["ea-roster-link", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("players")
        .select("id, display_name, ea_sports_id, ea_account_handle")
        .eq("team_id", teamId);
      if (error) throw error;
      return (data || []) as EaLinkRosterRow[];
    },
    staleTime: 60_000,
  });
  const rosterLookup = buildRosterLookup(rosterQuery.data || []);

  const clubQuery = useQuery({
    queryKey: ["ea-club", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("latest_ea_club", { _team_id: teamId });
      if (error) throw error;
      return (data as any) || null;
    },
    enabled: !!eaClubId,
  });
  const lastSyncQuery = useQuery({
    queryKey: ["ea-last-sync", teamId],
    queryFn: async () => {
      const { data } = await supabaseV2
        .from("ea_club_snapshots")
        .select("fetched_at")
        .eq("team_id", teamId)
        .order("fetched_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data?.fetched_at || null;
    },
    enabled: !!eaClubId,
  });
  const membersQuery = useQuery({
    queryKey: ["ea-members", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("list_ea_members", { _team_id: teamId });
      if (error) throw error;
      return (data || []) as Array<{ ea_persona: string; ea_name: string | null; data: any; fetched_at: string }>;
    },
    enabled: !!eaClubId,
  });
  const matchesQuery = useQuery({
    queryKey: ["ea-matches", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("ea_match_snapshots")
        .select("ea_match_id, played_at, data, fetched_at")
        .eq("team_id", teamId)
        .order("played_at", { ascending: false, nullsFirst: false })
        .limit(500);
      if (error) throw error;
      return (data || []) as Array<{ ea_match_id: string; played_at: string | null; data: any; fetched_at: string }>;
    },
    enabled: !!eaClubId,
  });

  // Cambio titolo EA: il club collegato e' del titolo precedente (sync fermo)
  // e/o lo storico contiene partite di piu' titoli. Il titolo scelto governa
  // tutta la vista: partite, e per un titolo passato anche KPI, top giocatori
  // e carriera, presi dall'ultimo snapshot completo del club di allora.
  const legacy = isLegacyEaLink({ ea_club_id: eaClubId, ea_game: eaGame });
  const linkedGameLabel = eaGameLabel(eaGame);
  const currentGameLabel = eaGameLabel(CURRENT_EA_GAME);
  const allMatches = matchesQuery.data || [];
  const gamesInHistory = Array.from(
    new Set(allMatches.map((m) => eaGameOf(m.data)).filter((g): g is string => !!g)),
  ).sort().reverse();
  const defaultGame = eaGame && gamesInHistory.includes(eaGame) ? eaGame : gamesInHistory[0] || null;
  const activeGame = gamesInHistory.length > 1 && gameFilter !== "all" ? gameFilter ?? defaultGame : null;
  const matches = activeGame
    ? allMatches.filter((m) => (eaGameOf(m.data) || activeGame) === activeGame)
    : allMatches;
  // Club di ogni titolo nello storico: l'our_club_id piu' frequente delle sue
  // partite (per il titolo collegato vale l'id del team).
  const clubIdByGame = (() => {
    const counts = new Map<string, Map<string, number>>();
    for (const m of allMatches) {
      const g = eaGameOf(m.data);
      const id = m.data?.our_club_id ? String(m.data.our_club_id) : null;
      if (!g || !id) continue;
      const byId = counts.get(g) || new Map<string, number>();
      byId.set(id, (byId.get(id) || 0) + 1);
      counts.set(g, byId);
    }
    const out = new Map<string, string>();
    for (const [g, byId] of counts) {
      let best: string | null = null;
      for (const [id, n] of byId) if (!best || n > (byId.get(best) || 0)) best = id;
      if (best) out.set(g, best);
    }
    if (eaGame && eaClubId) out.set(eaGame, eaClubId);
    return out;
  })();
  // Titoli passati presenti nello storico, con il club di allora.
  const archivedGames = gamesInHistory
    .filter((g) => g !== eaGame && clubIdByGame.has(g))
    .map((g) => ({ game: g, clubId: clubIdByGame.get(g)! }));
  const archivedClubId = activeGame && activeGame !== eaGame ? clubIdByGame.get(activeGame) || null : null;
  const archivedClubsQuery = useQuery({
    queryKey: ["ea-club-archive", teamId, archivedGames.map((a) => a.clubId).join(",")],
    queryFn: async () => {
      // Gli ultimi snapshot di un club del titolo precedente possono arrivare
      // dalla cache di proclubsanalytics senza overallStats: si prende
      // l'ultimo con le statistiche complete.
      const out = new Map<string, { data: any; fetched_at: string }>();
      for (const a of archivedGames) {
        const { data, error } = await supabaseV2
          .from("ea_club_snapshots")
          .select("data, fetched_at")
          .eq("team_id", teamId)
          .eq("ea_club_id", a.clubId)
          .not("data->overallStats->>gamesPlayed", "is", null)
          .order("fetched_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        if (data) out.set(a.clubId, data as { data: any; fetched_at: string });
      }
      return out;
    },
    enabled: archivedGames.length > 0,
    staleTime: 10 * 60_000,
  });
  const archivedClubQuery = {
    data: archivedClubId ? archivedClubsQuery.data?.get(archivedClubId) || null : null,
    isLoading: archivedClubsQuery.isLoading,
  };
  const viewingArchive = !!archivedClubId;

  const runSync = async (forceRefresh = false) => {
    setSyncing(true);
    setSyncMessage(t("world.syncing"));
    try {
      const { data, error: invokeError } = await supabaseV2.functions.invoke("sync-ea-club", {
        body: { teamId, forceRefresh },
      });
      if (invokeError) throw invokeError;
      const r = data as any;
      if (r?.legacy) {
        setSyncMessage(t("legacy.syncBlocked", { old: eaGameLabel(r.ea_game), current: eaGameLabel(r.current_game) }));
        return;
      }
      const errs = r?.errors || {};
      const errorList = Object.entries(errs).filter(([_, v]) => !!v).map(([k, v]) => `${k}: ${v}`);
      setSyncMessage(
        errorList.length > 0
          ? t("world.syncWarn", {
              members: r.saved?.membersSaved || 0,
              matches: r.saved?.matchesSaved || 0,
              errors: errorList.join(" · "),
            })
          : t("world.syncOk", {
              members: r.saved?.membersSaved || 0,
              matches: r.saved?.matchesSaved || 0,
            })
      );
      queryClient.invalidateQueries({ queryKey: ["ea-club", teamId] });
      queryClient.invalidateQueries({ queryKey: ["ea-members", teamId] });
      queryClient.invalidateQueries({ queryKey: ["ea-matches", teamId] });
      queryClient.invalidateQueries({ queryKey: ["ea-last-sync", teamId] });
    } catch (e: any) {
      setSyncMessage(t("world.syncError", { message: e?.message || String(e) }));
    } finally {
      setSyncing(false);
    }
  };

  // Considera "stale" se l'ultimo sync ha più di 24h.
  const lastSyncMs = lastSyncQuery.data ? new Date(lastSyncQuery.data).getTime() : null;
  const ageHours = lastSyncMs ? Math.floor((Date.now() - lastSyncMs) / 3_600_000) : null;
  const lastSyncLabel = !lastSyncMs
    ? "mai"
    : ageHours === 0
      ? "ora"
      : ageHours < 24
        ? `${ageHours}h fa`
        : `${Math.floor(ageHours / 24)}g fa`;
  const isStale = ageHours == null ? true : ageHours >= 24;

  if (!eaClubId) {
    return (
      <section className="rounded-[20px] bg-card p-5 max-w-xl">
        <h2 className="text-[17px] font-semibold">{t("noClubTitle")}</h2>
        <p className="mt-1 text-muted-foreground">{t("noClubDesc")}</p>
        {canManage && onOpenSettings && (
          <Button className="mt-4" onClick={onOpenSettings}>{t("noClubCta")}</Button>
        )}
      </section>
    );
  }

  // Il club collegato resta nell'intestazione; KPI e panoramica seguono il
  // titolo scelto nel filtro.
  const linkedInfo: any = clubQuery.data?.clubInfo || null;
  const club = viewingArchive ? archivedClubQuery.data?.data || null : clubQuery.data;
  const clubLoading = viewingArchive ? archivedClubQuery.isLoading : clubQuery.isLoading;
  const info: any = club?.clubInfo || null;
  const overall: any = club?.overallStats || null;
  const careers: any[] = club?.membersCareersStats?.members || [];
  const positionCount: any = club?.membersCurrentStats?.positionCount || {};
  const playoff: any[] = club?.playoffAchievements || [];
  const members: Array<{ ea_persona: string; ea_name: string | null; data: any }> = viewingArchive
    ? ((club?.membersCurrentStats?.members || []) as any[]).map((m) => ({
        ea_persona: String(m?.name || m?.proName || ""),
        ea_name: m?.proName || m?.name || null,
        data: m,
      }))
    : membersQuery.data || [];

  const friendlyMatches = matches.filter((m) => m.data?.match_type === "friendlyMatch");
  const leagueMatches = matches.filter((m) => m.data?.match_type === "leagueMatch");
  const playoffMatches = matches.filter((m) => m.data?.match_type === "playoffMatch");
  const seasonMatches = [...leagueMatches, ...playoffMatches];

  // "Tutti" con piu' titoli: i contatori (partite, risultati, gol, promozioni)
  // sono la somma dei club di ogni titolo; skill rating, divisione e
  // reputazione restano quelli del club collegato, perche' EA li assegna al
  // club e non si sommano.
  const combiningAll = activeGame === null && archivedGames.length > 0;
  const combinedParts: Array<{ game: string; overall: any }> = combiningAll
    ? [
        ...archivedGames
          .map((a) => ({ game: a.game, overall: archivedClubsQuery.data?.get(a.clubId)?.data?.overallStats }))
          .filter((p) => !!p.overall),
        ...(eaGame && overall ? [{ game: eaGame, overall }] : []),
      ].sort((a, b) => a.game.localeCompare(b.game))
    : [];
  const sumOf = (key: string) => combinedParts.reduce((acc, p) => acc + Number(p.overall?.[key] || 0), 0);
  const totals: any = combiningAll && combinedParts.length > 0
    ? {
        ...overall,
        gamesPlayed: sumOf("gamesPlayed"),
        leagueAppearances: sumOf("leagueAppearances"),
        wins: sumOf("wins"),
        ties: sumOf("ties"),
        losses: sumOf("losses"),
        goals: sumOf("goals"),
        goalsAgainst: sumOf("goalsAgainst"),
        promotions: sumOf("promotions"),
        relegations: sumOf("relegations"),
      }
    : overall;
  const gamesByTitle = combinedParts.map((p) => `${eaGameLabel(p.game)} ${Number(p.overall?.gamesPlayed || 0)}`).join(" · ");

  const winPct = totals && Number(totals.gamesPlayed) > 0
    ? Math.round((Number(totals.wins) / Number(totals.gamesPlayed)) * 100)
    : 0;
  const goalDiff = totals ? Number(totals.goals || 0) - Number(totals.goalsAgainst || 0) : 0;
  const goalsPerGame = totals && Number(totals.gamesPlayed) > 0
    ? (Number(totals.goals) / Number(totals.gamesPlayed)).toFixed(2)
    : "0";
  const concededPerGame = totals && Number(totals.gamesPlayed) > 0
    ? (Number(totals.goalsAgainst) / Number(totals.gamesPlayed)).toFixed(2)
    : "0";

  // Top scorers / assist / mom dai membri current
  const topScorers = [...members]
    .sort((a, b) => (Number(b.data?.goals || 0)) - (Number(a.data?.goals || 0)))
    .slice(0, 5);
  const topAssist = [...members]
    .sort((a, b) => (Number(b.data?.assists || 0)) - (Number(a.data?.assists || 0)))
    .slice(0, 5);
  const topRating = [...members]
    .sort((a, b) => (Number(b.data?.ratingAve || 0)) - (Number(a.data?.ratingAve || 0)))
    .slice(0, 5);

  return (
    <div className="space-y-4 animate-fade-in">
      {legacy && (
        <Card className="bg-[hsl(var(--warn-soft))]">
          <CardContent className="p-4 flex flex-wrap items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1 basis-[240px] space-y-1">
              <p className="font-semibold text-sm">{t("legacy.title", { old: linkedGameLabel })}</p>
              <p className="text-[13px] text-muted-foreground">
                {canManage
                  ? t("legacy.body", { old: linkedGameLabel, current: currentGameLabel })
                  : t("legacy.bodyViewer", { old: linkedGameLabel, current: currentGameLabel })}
              </p>
            </div>
            {canManage && onOpenSettings && (
              <Button size="sm" onClick={onOpenSettings} className="shrink-0">
                {t("legacy.cta", { current: currentGameLabel })}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Hero club */}
      <Card>
        <CardContent className="p-5 flex flex-wrap items-center gap-4">
          <div className="min-w-0 flex-1 basis-[200px]">
            <h2 className="text-[26px] font-bold leading-tight break-words">
              {linkedInfo?.name || `Club ${eaClubId}`}
            </h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              {t("hero.clubFc", { platform: eaPlatformLabel(eaPlatform) })}
              {eaGame ? ` · ${linkedGameLabel}` : ""}
            </p>
            <p className="text-[13px] text-[hsl(var(--faint))]">
              ID {eaClubId}
              {linkedInfo?.customKit?.stadName ? t("hero.stadium", { name: linkedInfo.customKit.stadName }) : ""}
              {overall?.bestDivision ? t("hero.bestDivision", { div: overall.bestDivision }) : ""}
            </p>
            <p className="text-xs mt-1 flex items-center gap-1.5">
              <span className={cn("inline-block w-2 h-2 rounded-full", isStale ? "bg-amber-500" : "bg-emerald-500")} />
              <span className="text-muted-foreground">
                {t("hero.lastUpdate")} <span className="font-medium text-foreground">{lastSyncLabel}</span>
                {isStale && lastSyncMs ? t("hero.stale") : ""}
              </span>
            </p>
          </div>
          {canManage && !legacy && (
            <div className="flex flex-col items-end gap-1 w-full sm:w-auto">
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Button
                  size="sm"
                  onClick={() => runSync(false)}
                  disabled={syncing}
                  className="btn-lift flex-1 sm:flex-none"
                >
                  {syncing ? t("buttons.syncing") : t("buttons.sync")}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={syncing}
                      title={t("buttons.advancedActionsTitle")}
                      aria-label={t("buttons.advancedActionsTitle")}
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="max-w-xs">
                    <DropdownMenuItem
                      onClick={() => runSync(true)}
                      className="flex-col items-start gap-0.5"
                    >
                      <span className="font-medium">{t("buttons.refreshSync")}</span>
                      <span className="text-xs text-muted-foreground leading-snug whitespace-normal">
                        {t("buttons.refreshSyncHint")}
                      </span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("buttons.autoSyncNote")}
              </p>
            </div>
          )}
          {syncMessage && (
            <p className="w-full text-xs text-muted-foreground mt-2 truncate">{syncMessage}</p>
          )}
        </CardContent>
      </Card>

      {/* Filtro per titolo EA, quando lo storico ne contiene piu' d'uno. */}
      {gamesInHistory.length > 1 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <div
            className="inline-flex max-w-full overflow-x-auto rounded-full bg-raised p-1 text-sm"
            role="group"
            aria-label={t("gameFilter.label")}
          >
            {[...gamesInHistory, "all"].map((g) => {
              const selected = g === "all" ? activeGame === null : activeGame === g;
              return (
                <button
                  key={g}
                  onClick={() => setGameFilter(g)}
                  aria-pressed={selected}
                  className={cn(
                    "px-3.5 py-1.5 rounded-full whitespace-nowrap font-medium transition-colors",
                    selected ? "bg-[hsl(var(--active))] text-foreground" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {g === "all" ? t("gameFilter.all") : eaGameLabel(g)}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            {viewingArchive && archivedClubQuery.data
              ? t("gameFilter.archiveNote", {
                  game: eaGameLabel(activeGame),
                  name: fixEaText(info?.name) || archivedClubId,
                  id: archivedClubId,
                  date: new Date(archivedClubQuery.data.fetched_at).toLocaleDateString(currentIntlLocale()),
                })
              : activeGame === null
                ? t("gameFilter.allNote", { games: combinedParts.map((p) => eaGameLabel(p.game)).join(" + ") || linkedGameLabel, game: linkedGameLabel })
                : ""}
          </p>
        </div>
      )}

      {clubLoading && <SectionLoading />}

      {!clubLoading && !club && (
        <Card>
          <CardContent className="p-6">
            <EmptyState text={t("world.noSnapshot")} />
          </CardContent>
        </Card>
      )}

      {overall && (
        <>
          {/* KPI strip */}
          <Card>
            <CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="flex flex-col gap-0.5">
                <span className="stat-label">{t("world.skill")}</span>
                <span className="stat-value">{overall.skillRating || "-"}</span>
                <span className="stat-hint">
                  {combiningAll ? `${linkedGameLabel} · ` : ""}
                  {t("world.currentDiv", { div: overall.currentDivision || "-" })}
                </span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="stat-label">{t("world.games")}</span>
                <span className="stat-value">{totals.gamesPlayed || 0}</span>
                <span className="stat-hint">
                  {combiningAll && combinedParts.length > 1
                    ? gamesByTitle
                    : t("world.leagueGames", { count: totals.leagueAppearances || 0 })}
                </span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="stat-label">{t("world.winRate")}</span>
                <span className="stat-value">{winPct}%</span>
                <span className="stat-hint">{t("world.record", { w: totals.wins, d: totals.ties || 0, l: totals.losses })}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="stat-label">{t("world.goalDiff")}</span>
                <span className={cn("stat-value", goalDiff > 0 && "text-[hsl(var(--ok))]", goalDiff < 0 && "text-[hsl(var(--danger))]")}>
                  {goalDiff > 0 ? "+" : ""}{goalDiff}
                </span>
                <span className="stat-hint">{totals.goals}/{totals.goalsAgainst}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="stat-label">{t("world.goalsPerGame")}</span>
                <span className="stat-value">{goalsPerGame}</span>
                <span className="stat-hint">{t("world.conceded", { value: concededPerGame })}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="stat-label">{t("world.promoRele")}</span>
                <span className="stat-value">{totals.promotions || 0}/{totals.relegations || 0}</span>
                <span className="stat-hint">
                  {combiningAll ? `${linkedGameLabel} · ` : ""}
                  {t("world.reputation", { value: overall.reputationtier || 0 })}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Tab switcher: Overview · Season/Playoff · Amichevoli */}
          <div className="inline-flex max-w-full overflow-x-auto rounded-full bg-raised p-1 text-sm">
            {(["overview", "season", "friendly"] as const).map((k) => {
              const label = k === "overview" ? t("world.tab.overview") : k === "season" ? t("world.tab.season") : t("world.tab.friendly");
              return (
                <button
                  key={k}
                  onClick={() => { track.tabChange("ea_world", k); setTab(k); }}
                  className={cn(
                    "px-3.5 py-1.5 rounded-full whitespace-nowrap font-medium transition-colors",
                    tab === k ? "bg-[hsl(var(--active))] text-foreground" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {tab === "overview" && (
            <div className="space-y-4">
              {combiningAll && (
                <p className="text-xs text-muted-foreground">{t("gameFilter.allPlayersNote", { game: linkedGameLabel })}</p>
              )}
              <div className="grid md:grid-cols-3 gap-4">
                <TopMembersCard title={t("sections.topGoals")} rows={topScorers} metric="goals" rosterLookup={rosterLookup} />
                <TopMembersCard title={t("sections.topAssist")} rows={topAssist} metric="assists" rosterLookup={rosterLookup} />
                <TopMembersCard title={t("sections.topRating")} rows={topRating} metric="ratingAve" rosterLookup={rosterLookup} />
              </div>

              {!viewingArchive && <EaClubSection teamId={teamId} eaClubId={eaClubId} />}

              {careers.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{t("sections.careerAllTime")}</CardTitle>
                    <CardDescription>
                      {t("sections.careerCumulativeDesc", {
                        def: positionCount.defender || 0,
                        mid: positionCount.midfielder || 0,
                        fwd: positionCount.forward || 0,
                        gk: positionCount.goalkeeper || 0,
                      })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-muted-foreground">
                          <tr>
                            <th className="text-left eb-data text-[13px] py-1 pr-2">{t("playerAgg.name")}</th>
                            <th className="text-left eb-data text-[13px] px-1">{t("playerAgg.pos")}</th>
                            <th className="text-right eb-data text-[13px] px-1">{t("playerAgg.pt")}</th>
                            <th className="text-right eb-data text-[13px] px-1">{t("playerAgg.g")}</th>
                            <th className="text-right eb-data text-[13px] px-1">{t("playerAgg.a")}</th>
                            <th className="text-right eb-data text-[13px] px-1">{t("playerAgg.mom")}</th>
                            <th className="text-right eb-data text-[13px] px-1">{t("playerAgg.rating")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {careers
                            .slice()
                            .sort((a: any, b: any) => Number(b.goals || 0) - Number(a.goals || 0))
                            .map((c: any) => (
                              <tr key={c.name} className="border-t">
                                <td className="py-1.5 pr-2 font-medium truncate">{c.name}</td>
                                <td className="px-1 text-xs capitalize text-muted-foreground">{c.favoritePosition || "-"}</td>
                                <td className="text-right font-tabular px-1">{c.gamesPlayed || 0}</td>
                                <td className="text-right font-tabular px-1 font-semibold">{c.goals || 0}</td>
                                <td className="text-right font-tabular px-1">{c.assists || 0}</td>
                                <td className="text-right font-tabular px-1">{c.manOfTheMatch || 0}</td>
                                <td className="text-right font-tabular px-1">
                                  {c.ratingAve ? Number(c.ratingAve).toFixed(2) : "-"}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {tab === "season" && (
            <SeasonPlayoffTab
              teamId={teamId}
              matches={seasonMatches}
              leagueMatches={leagueMatches}
              playoffMatches={playoffMatches}
              eaClubId={eaClubId}
              playoffAchievements={playoff}
            />
          )}

          {tab === "friendly" && (
            <FriendlyTab teamId={teamId} matches={friendlyMatches} eaClubId={eaClubId} />
          )}
        </>
      )}
    </div>
  );
}
